import type React from 'react';
import { useState } from 'react';
import {
  Card,
  CardContent,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@ais-ui/ui-core';
import { AtonStatusOption } from '../../types';
import { MonitorCardHeader } from './MonitorCardHeader';

interface StatusMonitorProps {
  name: string;
  description?: string;
  statuses: AtonStatusOption[];
  currentValue?: number; // The current status value (0, 1, 2, 3, etc.)
  className?: string;
}

const getStateVariantStyles = (variant: string, isActive: boolean) => {
  const baseStyles =
    'transition-all duration-200 p-3 rounded-lg border-2 flex items-center gap-3';

  if (!isActive) {
    return `${baseStyles} opacity-50 hover:opacity-75 bg-muted border-border`;
  }

  switch (variant) {
    case 'success':
      return `${baseStyles} bg-green-50 text-green-700 border-green-200 hover:bg-green-100`;
    case 'destructive':
      return `${baseStyles} bg-red-50 text-red-700 border-red-200 hover:bg-red-100`;
    case 'warning':
      return `${baseStyles} bg-yellow-50 text-yellow-700 border-yellow-200 hover:bg-yellow-100`;
    case 'secondary':
      return `${baseStyles} bg-muted text-muted-foreground border-border hover:bg-muted`;
    default:
      return `${baseStyles} bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100`;
  }
};

export function StatusMonitor({
  name,
  description,
  statuses,
  currentValue,
  className = '',
}: StatusMonitorProps) {
  // Find active status based on currentValue (index in statuses array)
  const activeStatusIndex =
    currentValue !== undefined && currentValue < statuses.length
      ? currentValue
      : 0; // Default to first status

  const activeStatus = statuses[activeStatusIndex];

  if (!statuses.length) {
    return null;
  }

  return (
    <Card className={`w-full h-full ${className}`}>
      <MonitorCardHeader
        name={name}
        description={description}
        statusIcon={activeStatus?.icon}
      />
      <CardContent className="flex-1 flex flex-col space-y-2 h-[calc(100%-72px)] justify-center">
        {/* Show only the active status - horizontally centered */}
        <div
          className={`${getStateVariantStyles(
            activeStatus.variant,
            true
          )} justify-center`}
        >
          <div className="flex-shrink-0">{activeStatus.icon}</div>
          <div className="flex-1">
            <div className="font-medium text-sm">{activeStatus.label}</div>
            {activeStatus.description && (
              <div className="text-xs opacity-75 mt-1">
                {activeStatus.description}
              </div>
            )}
          </div>
        </div>

        {/* Popover trigger to show all statuses - horizontally centered */}
        {statuses.length > 1 && (
          <div className="flex">
            <Popover>
              <PopoverTrigger asChild>
                <button className="text-xs text-muted-foreground hover:text-foreground transition-colors cursor-pointer underline-offset-2 hover:underline">
                  Show available statuses
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-3" align="start">
                <div className="space-y-2">
                  <div className="text-sm font-medium text-foreground mb-3">
                    Available Statuses for {name}
                  </div>
                  {statuses.map((status, index) => (
                    <div
                      key={status.id}
                      className={getStateVariantStyles(
                        status.variant,
                        index === activeStatusIndex
                      )}
                    >
                      <div className="flex-shrink-0">{status.icon}</div>
                      <div className="flex-1">
                        <div className="font-medium text-sm">
                          {status.label}
                        </div>
                        {index === activeStatusIndex && (
                          <div className="text-xs opacity-75 mt-1">
                            Current Status
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
